import HttpRequest from '@/config/axios.config'
import { savePdf } from '@/utils/filesaver'
import { isEmpty, pick } from 'lodash'
import { InspectionReportSectionEnum } from '@/constant/qsf'

type HeaderType = {
  headers: {
    'content-disposition'?: string
  }
}

type AddEditPayloadType = {
  comment: string
  originalId: string
  thumbnailId: string
}

type AddPhotoPayloadType = AddEditPayloadType & {
  type: InspectionReportSectionEnum
  key: string
}

type UpdatePhotoPayloadType = AddEditPayloadType & {
  type: InspectionReportSectionEnum
  mediaId?: string
}

export async function getInspectionReport(id: string) {
  const data = await HttpRequest.get(`/inspections/${id}/report`)
  return data.data.data
}

export async function getInspectionComments(id: string) {
  const data = await HttpRequest.get(`/inspections/${id}/comments`)
  // comments has Inspection and Overall Comments
  const pickFields = ['sections', 'comments']
  const pickItem = pick(data.data.data, pickFields)
  return pickItem
}

export async function getInspectionReportHeader(id: string) {
  const data = await HttpRequest.get(`/inspections/${id}/report-header`)
  return data.data.data
}

const getHeaderContentDisposition = (dataContent: HeaderType) => {
  const contentDisposition = dataContent?.headers?.['content-disposition'] || ''
  if (!contentDisposition) return ''

  let fileName = contentDisposition.split('filename=')[1]

  fileName = fileName.replace(/"/g, '')

  // Keep the server's extension — generate-report returns .pdf for most
  // report types but .zip for FES/TUV multi-file reports.
  return fileName
}

export async function getGeneratedPdf(inspectionId: string, options = {}) {
  const data: any = await HttpRequest.post(
    '/inspections/generate-report',
    {
      inspectionId,
      options,
    },
    {
      responseType: 'arraybuffer',
      timeout: 15 * 60 * 1000,
    }
  ).catch((error) => console.log('error', error))

  const fileName = getHeaderContentDisposition(data)
  const blob = new Blob([data.data])

  // The header filename already has its extension (.pdf or .zip); only
  // append .pdf when falling back to the generated default name.
  savePdf(blob, fileName, fileName ? '' : '.pdf')
}

export async function saveDecisionRef(
  id: string,
  decision: string,
  comment: string
) {
  const data = await HttpRequest.post(`/inspections/${id}/decision`, {
    decision,
    comment,
  })
  return data.data
}

export async function saveSectionRef(
  inspection_id: string,
  section_id: string,
  result: string,
  comment: string
) {
  const data = await HttpRequest.patch(
    `/inspections/${inspection_id}/section/${section_id}`,
    {
      result,
      comment,
    }
  )
  return data.data
}

export async function saveInspectionResultRef(
  inspection_id: string,
  result: string,
  comment: string
) {
  const data = await HttpRequest.patch(`/inspections/${inspection_id}/result`, {
    result,
    comment,
  })
  return data.data
}

export const reportCompanySettingRef = async (company_id) => {
  return await HttpRequest.get(`/company-settings?name=order`, {
    params: { company_id },
  })
}

export const defectDetailRef = async (id: string) => {
  try {
    const data = await HttpRequest.get(`/defects/${id}`, {
      params: {
        with_list: true,
      },
    })
    return data.data
  } catch (error) {
    throw error
  }
}

export const updateDefectRef = async (id: string, payload) => {
  try {
    return await HttpRequest.patch(`/defects/${id}`, payload)
  } catch (error: any) {
    const { data } = error.response
    throw data
  }
}

export const deleteDefectRef = async (id: string) => {
  return await HttpRequest.delete(`/defects/${id}`)
}

export const taskDetailRef = async (id: string) => {
  try {
    const { data } = await HttpRequest.get(`/tasks/${id}`)
    if (isEmpty(data.data)) {
      throw new Error('The checkpoint not found')
    }
    return data
  } catch (error) {
    throw error
  }
}

export const updatePhotoCommentRef = async (
  id: string,
  payload: UpdatePhotoPayloadType
) => {
  try {
    const { type } = payload

    if (type === InspectionReportSectionEnum.CHECKPOINT) {
      const { data } = await HttpRequest.patch(`/tasks/${id}/comment`, payload)
      if (isEmpty(data.data)) {
        throw new Error('Task can not update')
      }
      return data
    } else if (type === InspectionReportSectionEnum.DEFECT) {
      return await HttpRequest.patch(`/defects/${id}/comment`, payload)
    }
  } catch (error) {
    throw error
  }
}

export const addNewPhotoCommentRef = async (
  id: string,
  payload: AddPhotoPayloadType
) => {
  try {
    const { type } = payload
    if (type === InspectionReportSectionEnum.CHECKPOINT) {
      return await HttpRequest.post(
        `/tasks/${id}/add-new-photo-comment`,
        payload
      )
    } else if (type === InspectionReportSectionEnum.DEFECT) {
      return await HttpRequest.post(
        `/defects/${id}/add-new-photo-comment`,
        payload
      )
    }
  } catch (error) {
    throw error
  }
}

export const getDefectAreaRef = async () => {
  return await HttpRequest.get(`/defect-groups/area`)
}

export const deletePhotoComment = async (
  id: string,
  originalId: string,
  type: InspectionReportSectionEnum
) => {
  if (type === InspectionReportSectionEnum.CHECKPOINT) {
    return await HttpRequest.delete(`/tasks/${id}/comment/${originalId}`)
  } else if (type === InspectionReportSectionEnum.DEFECT) {
    return await HttpRequest.delete(`/defects/${id}/comment/${originalId}`)
  }
}

export const inspectionUpdatesRef = async (id: string, params) => {
  return await HttpRequest.get(`activities/${id}/inspection_logs`, {
    params: params,
  })
}

export async function approveInspectionRef(id: string, content: any) {
  return await HttpRequest.patch(`/inspections/${id}/approve`, {
    content,
  })
}

export async function unlockApprovedInspectionRef(id: string) {
  return await HttpRequest.patch(`/inspections/${id}/unlock-approved`)
}

export async function uploadInspectionTaskPhoto(payload: any) {
  try {
    const { data } = await HttpRequest.post(`/inspections/image`, payload)
    if (isEmpty(data.data)) {
      throw new Error('Image can not upload')
    }
    return data.data
  } catch (error) {
    throw error
  }
}
